// Шаг между диагностикой и первым днём: сколько дней в неделю.
//
// Единственное число в системе, которое задаёт сам ученик. Это не настройка и
// не опрос: решение 2 — автономия единственная мотивационная потребность с
// приличным эффектом, доступная тому, кто занимается один, и ей нужна хотя бы
// одна настоящая точка приложения. Дефолт, поставленный молча, отнимал бы её.
//
// Почему это спрашивается ЗДЕСЬ, а не в настройках: диагностика только что
// кончилась, ученик впервые видит, где он на карте, и обязательство берётся
// один раз — на входе, пока оно ещё решение, а не отчёт.
//
// Требуемый темп из этого числа не считается. Темп — это остаток карты на дни
// до экзамена, и его показывает недельная полоса. Обязательство отвечает на
// другой вопрос: сколько дней ученик готов занять, а из него считается мера
// соблюдения — «12 из последних 21» при четырёх днях вместо «18 из 21».

import { useState } from "react";
import { api, useMutation, useQuery } from "../api";
import { plural } from "../lib/dates";

/**
 * Три дня — нижняя граница того, что имеет смысл предлагать: реже интервальное
 * повторение не работает, и обещать по нему нечего. Семь — верхняя. Шесть
 * выделено как то, из чего считались все цифры плана: 18 из 21.
 */
const CHOICES = [3, 4, 5, 6, 7];

export function Commitment({
  learnerKey,
  onDone,
}: {
  learnerKey: string;
  onDone: () => void;
}) {
  const set = useMutation(api.goal.set);
  // Название цели подготовки — из строки ученика, а не константой (30 октября 2026).
  const goal = useQuery(api.goal.current, { learnerKey });
  const [picked, setPicked] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const confirm = (days: number) => {
    setBusy(true);
    setFailed(null);
    void set({ learnerKey, daysPerWeek: days })
      .then(onDone)
      .catch((e: unknown) => {
        setBusy(false);
        setFailed(e instanceof Error ? e.message : String(e));
      });
  };

  return (
    <div className="cols cols--solo">
      <div className="col">
        <div className="eyebrow">{goal?.examTitle ?? "\u00a0"}</div>
        <h1 className="h1">Сколько дней в неделю</h1>
        <p className="lede">
          Одно решение, и его принимаешь ты. Всё остальное — что повторять,
          что открыть, сколько задач — система считает сама.
        </p>

        <div className="card" style={{ marginTop: 16 }}>
          <div className="card__label">Занимаюсь</div>
          <div className="chips" style={{ marginTop: 14 }}>
            {CHOICES.map((days) => (
              <button
                key={days}
                className="chip"
                aria-pressed={picked === days}
                onClick={() => setPicked(days)}
              >
                {days} {plural(days, "день", "дня", "дней")}
              </button>
            ))}
          </div>

          {picked === null ? null : (
            <div className="muted" style={{ marginTop: 16, fontSize: 14, lineHeight: 1.6 }}>
              Мера соблюдения станет «
              <span className="num">{Math.round((picked * 21) / 7)}</span> из
              последних <span className="num">21</span>». Пропущенный день её
              почти не двигает — считается окно, а не серия подряд, и рвать
              нечего.
            </div>
          )}

          <div className="faint" style={{ marginTop: 14, lineHeight: 1.6 }}>
            Переписать можно не чаще раза в неделю: обязательство, которое
            переписывается в плохой вечер, не обязательство. Плохой вечер
            закрывает короткий режим на 8 минут — он есть всегда.
          </div>

          {failed === null ? null : (
            <div className="notice" style={{ marginTop: 14 }}>
              {failed}
            </div>
          )}

          <div className="row row--actions" style={{ marginTop: 18 }}>
            <button
              className="btn btn--primary btn--big"
              disabled={picked === null || busy}
              onClick={() => picked !== null && confirm(picked)}
            >
              {busy ? "Записываю" : "Беру на себя"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
